<template>
  <div class="sp-reorder-list-item">
    <span v-if="editable" class="sp-reorder-list-item__grip" aria-hidden="true">
      <sp-icon name="grip"></sp-icon>
    </span>
    <div class="sp-reorder-list-item__content">
      <slot />
    </div>
    <div v-if="editable" class="sp-reorder-list-item__moves">
      <sp-button
        class="sp-reorder-list-item__move-up"
        variant="text"
        color="text"
        densitysmcompact="false"
        icon="chevron-up-thin"
        :accessible-name="moveUpLabel"
        :aria-disabled="moveUpDisabled"
        @click="moveUp"
      ></sp-button>
      <sp-button
        class="sp-reorder-list-item__move-down"
        variant="text"
        color="text"
        densitysmcompact="false"
        icon="chevron-down-thin"
        :accessible-name="moveDownLabel"
        :aria-disabled="moveDownDisabled"
        @click="moveDown"
      ></sp-button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, useHost } from "vue";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { tAccessibleName } from "#ce/shared/components-i18n";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

const props = defineProps({
  /**
   * The value `update-order` reports for this row.
   *
   * @type {String}
   */
  value: {
    type: String,
    required: true,
  },
  /**
   * Names the row in its move buttons and in the move announcement. Give each row a different label,
   * so no two rows' move buttons share a name.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: "",
  },
  // `first`, `last` and `readonly` are set by the parent `sp-reorder-list`. Don't set them on a row.
  /** @type {Boolean|String} */
  first: booleanAttribute(),
  /** @type {Boolean|String} */
  last: booleanAttribute(),
  /** @type {Boolean|String} */
  readonly: booleanAttribute(),
});

defineEmits(["request-move"]);
const { emitBubbling } = useHostEmit();

useHost().setAttribute("role", "listitem");

const editable = computed(() => !toBoolean(props.readonly));
const moveUpLabel = computed(() => tAccessibleName("reorder_list_move_up", { label: props.label }));
const moveDownLabel = computed(() => tAccessibleName("reorder_list_move_down", { label: props.label }));
const moveUpDisabled = computed(() => String(toBoolean(props.first)));
const moveDownDisabled = computed(() => String(toBoolean(props.last)));

function moveUp(event) {
  requestMove(-1, event.currentTarget);
}

function moveDown(event) {
  requestMove(1, event.currentTarget);
}

// Asks the list to move the row, then puts focus back on the pressed button, so the next press keeps
// moving the same row. Moving the row, or the page re-rendering its rows in the new order, takes
// focus out of it.
async function requestMove(step, button) {
  emitBubbling("request-move", step);
  await nextTick();
  button.focus();
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-reorder-list-item {
  --reorder-list-item-control-color: var(--sp-comp-reorder-list-control-color, var(--sp-sys-color-on-surface-variant));
  display: flex;
  align-items: var(--reorder-list-controls-align, center);
  gap: var(--reorder-list-item-column-gap);
  padding: var(--reorder-list-item-padding-block) var(--reorder-list-item-padding-inline);

  &__grip {
    --sp-comp-icon-size: var(--sp-comp-reorder-list-grip-size, var(--sp-ref-scale-20));
    --sp-comp-icon-fill: var(--reorder-list-item-control-color);
    display: flex;
    flex: none;
    align-items: center;
    height: var(--reorder-list-controls-height, auto);
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__moves {
    display: flex;
    flex: none;
    align-items: center;
    gap: var(--sp-comp-reorder-list-move-gap, var(--sp-ref-scale-4));
    height: var(--reorder-list-controls-height, auto);
    cursor: default;
  }

  &__moves sp-button {
    --sp-comp-button-size: var(--sp-comp-reorder-list-move-size, var(--sp-ref-scale-28));
    --sp-comp-button-icon-size: var(--sp-comp-reorder-list-move-icon-size, var(--sp-ref-scale-20));
    --sp-comp-button-icon-fill-color: var(--reorder-list-item-control-color);

    &[aria-disabled="true"] {
      --sp-comp-button-icon-fill-color: var(--sp-comp-reorder-list-move-disabled-color, var(--sp-sys-color-disabled));
      --sp-comp-button-text-hover-variant-text-bg-opacity: 0;
    }
  }
}
</style>
